import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    inject,
    Injector,
    Input,
    OnInit,
    Output,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { IFiltersActivated } from '../_shared/models/filters-activated.model';
import { Filters, IFilter, IFilterValue } from '../_shared/models/filters.model';
import { BadgeComponent } from '../badge/badge.component';
import { IconComponent } from '../icon/icon.component';
import { FilterAppliedFilterComponent } from './filter-applied-filters/filter-applied-filter.component';
import { FilterDropdownComponent } from './filter-dropdown/filter-dropdown.component';

@Component({
    selector: 'sirius-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FilterAppliedFilterComponent,
        BadgeComponent,
        FilterDropdownComponent,
        CommonModule,
        IconComponent,
        TranslateModule,
    ]
})
export class FilterComponent implements OnInit {
    @Input() config?: IFilter[];
    @Input() filters: IFilter[] = [];
    @Input() showAppliedChips = true;

    @Output() selectionChange = new EventEmitter<Filters>();
    @Output() activatedFilters = new EventEmitter<IFiltersActivated>();
    @Output() filterUpdated = new EventEmitter<IFilter[]>();

    @ViewChild(FilterDropdownComponent) childComponents?: FilterDropdownComponent;

    selectedFilter = -1;

    private readonly _injector = inject(Injector);
    private readonly _platformId = inject(PLATFORM_ID);

    @HostListener('keydown.escape', ['$event'])
    handleEscape(event: Event) {
        if (this.selectedFilter < 0) {
            return;
        }
        event.stopPropagation();
        this.closeDropdown();
    }

    ngOnInit(): void {
        if (this.config) {
            this.filters = this.config;
        }
    }

    onFiltersActivated(values: IFilterValue[], category: string, categoryId: string): void {
        this.activatedFilters.emit({ category: category, id: categoryId, values: values });
    }

    toggleDropdown(index: number): void {
        if (index === this.selectedFilter) {
            this.closeDropdown();
        } else {
            this.openDropdown(index);
        }
    }

    onFilterToggleKeydown(event: KeyboardEvent, index: number): void {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.openDropdown(index);
                this.focusDropdownOption('first');
                return;
            case 'ArrowUp':
                event.preventDefault();
                this.openDropdown(index);
                this.focusDropdownOption('last');
                return;
            case 'Enter':
            case ' ':
            case 'Spacebar':
                event.preventDefault();
                if (this.isFilterSelected(index)) {
                    this.closeDropdown();
                } else {
                    this.openDropdown(index);
                    this.focusDropdownOption('first');
                }
                return;
            default:
                return;
        }
    }

    getFilterById(id: string): IFilter | undefined {
        return this.filters.find((filter) => filter.id === id);
    }

    getFilterValueById(filterId: string, valueId: string): IFilterValue | undefined {
        const filter = this.getFilterById(filterId);
        return filter?.values.find((filterValue) => filterValue.id === valueId);
    }

    deactivateFilterValue(filterId: string, valueId: string): void {
        const filter = this.getFilterById(filterId);
        if (!filter) {
            return;
        }

        const filterValue = this.getFilterValueById(filter.id, valueId);
        if (!filterValue?.active) {
            return;
        }

        filterValue.active = false;
        filter.activeCount--;

        this.setFilterValues();

        return;
    }

    openDropdown(index: number): void {
        this.selectedFilter = index;
    }

    closeDropdown(): void {
        this.selectedFilter = -1;
    }

    onDropdownClose(trigger: HTMLButtonElement): void {
        this.closeDropdown();
        this.focusIfBrowser(trigger);
    }

    getDropdownId(index: number): string {
        return `sir-filter-dropdown-${index}`;
    }

    getFilterToggleId(filterId: string): string {
        return `sir-filter-toggle-${filterId}`;
    }

    private focusIfBrowser(element: HTMLElement): void {
        if (!isPlatformBrowser(this._platformId)) return;
        element.focus();
    }

    private focusDropdownOption(target: 'first' | 'last'): void {
        afterNextRender(
            () => {
                if (!this.childComponents) return;

                if (target === 'last') {
                    this.childComponents.focusLastOption();
                    return;
                }

                this.childComponents.focusFirstActiveOrFirstOption();
            },
            { injector: this._injector }
        );
    }

    isFilterSelected(index: number): boolean {
        return this.selectedFilter === index;
    }

    /** Stable array ref for the open category — avoids resyncing the dropdown every change detection. */
    get openFilterValues(): IFilterValue[] {
        if (this.selectedFilter < 0 || this.selectedFilter >= this.filters.length) return [];
        return this.filters[this.selectedFilter].values;
    }

    setFilterValues(): void {
        this.filters = this.filters.map((filter) => ({
            ...filter,
            activeCount: filter.values.filter((v) => v.active).length,
        }));
        this.selectionChange.emit(new Filters(this.filters));
    }

    deactivateAllFilters(): void {
        this.filters.forEach((filter) => {
            filter.activeCount = 0;
            filter.values.forEach((filterValue) => {
                filterValue.active = false;
            });
        });

        this.setFilterValues();
    }

    handleBlur(event: FocusEvent): void {
        const currentTarget = event.currentTarget as HTMLElement;
        const relatedTarget = event.relatedTarget as HTMLElement | null;

        if (relatedTarget && !currentTarget.contains(relatedTarget)) {
            this.childComponents?.saveSelection();
            return;
        }

        if (!relatedTarget) {
            requestAnimationFrame(() => {
                if (document.hasFocus() && !currentTarget.contains(document.activeElement)) {
                    this.childComponents?.saveSelection();
                }
            });
        }
    }
}
